import { type JSX, useMemo } from 'react';

import { Skeleton, TableCell, TableRow } from '@mui/material';
import { useTranslation } from 'react-i18next';

const DEFAULT_ROW_COUNT = 8;
const MIN_CELL_WIDTH_PCT = 60;
const MAX_CELL_WIDTH_PCT = 95;
const SHIMMER_ANIMATION = 'pulse' as const;

export interface TableSkeletonProps {
  rows?: number;
  columnWidths: ReadonlyArray<string>;
  ariaLabel?: string;
}

/**
 * Skeleton rows for a table during initial fetch or pagination. One row
 * per `rows` slot, one Skeleton text cell per `columnWidths` entry; each
 * cell width is randomised within MIN..MAX% to look organic. Row height
 * matches a small dense table cell so the table doesn't shift when real
 * rows swap in.
 */
export const TableSkeleton = ({
  rows = DEFAULT_ROW_COUNT,
  columnWidths,
  ariaLabel,
}: TableSkeletonProps): JSX.Element => {
  const { t } = useTranslation();
  const widths = useMemo(
    () => buildRowWidthMatrix(rows, columnWidths.length),
    [rows, columnWidths.length],
  );
  return (
    <>
      {Array.from({ length: rows }, (_, rowIdx) => (
        <TableRow
          key={`skeleton-row-${rowIdx}`}
          aria-busy="true"
          aria-label={ariaLabel ?? t('loading')}
          data-testid="table-skeleton-row"
        >
          {columnWidths.map((_w, colIdx) => (
            <TableCell key={`skeleton-cell-${rowIdx}-${colIdx}`}>
              <Skeleton
                variant="text"
                animation={SHIMMER_ANIMATION}
                width={`${widths[rowIdx]?.[colIdx] ?? MAX_CELL_WIDTH_PCT}%`}
                sx={{ fontSize: 13 }}
              />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </>
  );
};

const buildRowWidthMatrix = (rows: number, cols: number): number[][] => {
  const range = MAX_CELL_WIDTH_PCT - MIN_CELL_WIDTH_PCT;
  const matrix: number[][] = [];
  for (let r = 0; r < rows; r++) {
    const row: number[] = [];
    for (let c = 0; c < cols; c++) {
      row.push(MIN_CELL_WIDTH_PCT + Math.floor(Math.random() * range));
    }
    matrix.push(row);
  }
  return matrix;
};
